import { useState, useEffect } from 'react';

export interface CalculationHistoryItem {
  id: string;
  type: string;
  inputs: Record<string, string | number>;
  result: string;
  timestamp: number;
}

const HISTORY_KEY = 'percentage_calculator_history';
const FAVORITES_KEY = 'percentage_calculator_favorites';
const MAX_HISTORY_ITEMS = 5;

export const useCalculationHistory = () => {
  const [history, setHistory] = useState<CalculationHistoryItem[]>([]);
  const [favorites, setFavorites] = useState<string[]>([]);

  // Load history and favorites on mount
  useEffect(() => {
    const savedHistory = localStorage.getItem(HISTORY_KEY);
    const savedFavorites = sessionStorage.getItem(FAVORITES_KEY);
    
    if (savedHistory) {
      setHistory(JSON.parse(savedHistory));
    }
    
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, []);

  const addCalculation = (
    type: string,
    inputs: Record<string, string | number>,
    result: string
  ) => {
    const newItem: CalculationHistoryItem = {
      id: Date.now().toString(),
      type,
      inputs,
      result,
      timestamp: Date.now(),
    };

    setHistory(prevHistory => {
      // Remove duplicate if same calculation exists
      const filteredHistory = prevHistory.filter(item => 
        !(item.type === type && JSON.stringify(item.inputs) === JSON.stringify(inputs))
      );
      
      const newHistory = [newItem, ...filteredHistory].slice(0, MAX_HISTORY_ITEMS);
      
      // Save to localStorage
      localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory));
      
      return newHistory;
    });
  };

  const toggleFavorite = (id: string) => {
    setFavorites(prevFavorites => {
      const newFavorites = prevFavorites.includes(id)
        ? prevFavorites.filter(favId => favId !== id)
        : [...prevFavorites, id];
      
      // Save to sessionStorage (session-based favorites)
      sessionStorage.setItem(FAVORITES_KEY, JSON.stringify(newFavorites));
      
      return newFavorites;
    });
  };

  const clearHistory = () => {
    setHistory([]);
    setFavorites([]);
    localStorage.removeItem(HISTORY_KEY);
    sessionStorage.removeItem(FAVORITES_KEY);
  };

  return {
    history,
    favorites,
    addCalculation,
    toggleFavorite,
    clearHistory,
  };
};